import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { AutoComplete } from 'primereact/autocomplete';
import '../../css/Pages.css';
import BookPublicationListing from '../../components/ScientificManagement/BookPublication/BookPublicationListing';
import BookPublicationForm from '../../components/ScientificManagement/BookPublication/BookPublicationForm';

const superCleanString = (str) => {
    if (!str) return '';
    return str.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
};

const PublicBookPublication = () => {
    const toast = useRef(null);
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [modalMode, setModalMode] = useState(null);
    const [formData, setFormData] = useState({});
    const [members, setMembers] = useState([]);

    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [filteredAuthors, setFilteredAuthors] = useState([]);

    const [filters, setFilters] = useState({
        authorName: '', keyword: '', status: 'Đã duyệt',
        fromDate: null, toDate: null
    });
    const [sortConfig, setSortConfig] = useState({ key: 'PublicationDate', direction: 'desc' });

    useEffect(() => {
        const fetchPublicData = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/publicdashboard/books`);
                if (response.ok) {
                    const result = await response.json();
                    setData(result);
                }
            } catch (error) {
                console.error("Lỗi tải dữ liệu", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchPublicData();
    }, []);

    const authorOptions = useMemo(() => {
        const authors = new Set();
        data.forEach(item => {
            if (item.FullName) authors.add(item.FullName.trim());
            try {
                const membersList = JSON.parse(item.MembersJSON || '[]');
                membersList.forEach(m => {
                    if (m.FullName || m.fullName) {
                        authors.add((m.FullName || m.fullName).trim());
                    }
                });
            } catch (e) { }
        });
        return Array.from(authors)
            .map(name => ({ label: name, value: name }))
            .sort((a, b) => a.label.localeCompare(b.label, 'vi'));
    }, [data]);

    const searchAuthor = (event) => {
        const query = superCleanString(event.query);
        if (!query.trim().length) {
            setFilteredAuthors([...authorOptions]);
        } else {
            setFilteredAuthors(authorOptions.filter(a => superCleanString(a.label).includes(query)));
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const filteredData = useMemo(() => {
        let result = data.filter(item => {
            if (filters.authorName) {
                const targetName = superCleanString(filters.authorName);
                let isMatch = superCleanString(item.FullName).includes(targetName);
                if (!isMatch) {
                    try {
                        const mList = JSON.parse(item.MembersJSON || '[]');
                        isMatch = mList.some(m => superCleanString(m.FullName || m.fullName).includes(targetName));
                    } catch (e) { }
                }
                if (!isMatch) return false;
            }

            if (filters.keyword) {
                const searchKeyword = superCleanString(filters.keyword);
                const bookTitle = superCleanString(item.Title);
                const publisher = superCleanString(item.Publisher);
                if (!bookTitle.includes(searchKeyword) && !publisher.includes(searchKeyword)) return false;
            }

            if (filters.fromDate || filters.toDate) {
                if (!item.PublicationDate) return false;
                const itemDate = new Date(item.PublicationDate);
                itemDate.setHours(0, 0, 0, 0);
                if (filters.fromDate && itemDate < new Date(filters.fromDate)) return false;
                if (filters.toDate && itemDate > new Date(filters.toDate)) return false;
            }
            return true;
        });

        if (sortConfig) {
            result.sort((a, b) => {
                if (sortConfig.key === 'PublicationDate') {
                    const dateA = a.PublicationDate ? new Date(a.PublicationDate).getTime() : 0;
                    const dateB = b.PublicationDate ? new Date(b.PublicationDate).getTime() : 0;
                    return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
                } else {
                    const valA = (a[sortConfig.key] || '').toString().trim();
                    const valB = (b[sortConfig.key] || '').toString().trim();
                    return sortConfig.direction === 'asc' ? valA.localeCompare(valB, 'vi') : valB.localeCompare(valA, 'vi');
                }
            });
        }
        return result;
    }, [data, filters, sortConfig]);

    const handleViewDetail = (item) => {
        let parsedMembers = [];
        try { parsedMembers = JSON.parse(item.MembersJSON || '[]'); } catch (e) { parsedMembers = []; }
        setFormData(item);
        setMembers(parsedMembers);
        setModalMode('view');
    };

    return (
        <div className="page-container" style={{ padding: '30px', maxWidth: '1400px', margin: '0 auto' }}>
            <Toast ref={toast} position="top-right" />
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ textTransform: 'uppercase', color: '#1e3a8a', margin: 0 }}>Danh sách Sách - Giáo trình đã công bố</h2>
                <button className="btn-cancel" onClick={() => window.history.back()} style={{ margin: 0 }}>
                    <i className="fa-solid fa-arrow-left"></i> Trở về Dashboard
                </button>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px' }}>

                    <div style={{ width: '100%' }}>
                        <AutoComplete
                            value={selectedAuthor}
                            suggestions={filteredAuthors}
                            completeMethod={searchAuthor}
                            field="label"
                            dropdown
                            placeholder="Họ tên giảng viên"
                            onChange={(e) => {
                                setSelectedAuthor(e.value);
                                setFilters(prev => ({ ...prev, authorName: e.value ? (e.value.value || e.value) : '' }));
                            }}
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ width: '100%', padding: '0 12px', border: '1px solid #ced4da', borderRadius: '4px', fontFamily: 'inherit' }}
                            panelStyle={{ width: 'auto', minWidth: '250px', maxWidth: '400px' }}
                            emptyMessage="Không tìm thấy giảng viên"
                        />
                    </div>

                    <Calendar
                        value={filters.fromDate}
                        onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.value }))}
                        placeholder="Từ ngày xuất bản"
                        dateFormat="dd/mm/yy"
                        showIcon
                        className="custom-prime-calendar"
                        style={{ width: '100%', height: '40px' }}
                        inputStyle={{ paddingLeft: '12px' }}
                    />

                    <Calendar
                        value={filters.toDate}
                        onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.value }))}
                        placeholder="Đến ngày xuất bản"
                        dateFormat="dd/mm/yy"
                        showIcon
                        className="custom-prime-calendar"
                        style={{ width: '100%', height: '40px' }}
                        inputStyle={{ paddingLeft: '12px' }}
                    />

                    <div className="search-wrapper" style={{ position: 'relative', width: '100%', gridColumn: '1 / -1' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888' }}></i>
                        <input type="text" name="keyword" placeholder="Tiêu đề sách hoặc nhà xuất bản" className="form-input" style={{ paddingLeft: '35px', width: '100%', height: '40px' }} value={filters.keyword} onChange={handleFilterChange} />
                    </div>
                </div>
            </div>

            <BookPublicationListing
                data={filteredData}
                isPublicView={true}
                onViewDetail={handleViewDetail}
                isLoading={isLoading}
                sortConfig={sortConfig}
                requestSort={(key) => setSortConfig({ key, direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}
            />

            <BookPublicationForm
                isOpen={modalMode === 'view'}
                onClose={() => setModalMode(null)}
                formData={formData}
                readOnly={true}
                members={members}
                users={[]}
                projectTypes={[]}
                educationLevels={[]}
                languages={[]}
                countries={[]}
            />
        </div>
    );
};

export default PublicBookPublication;